import React, { useState, useMemo } from 'react';
import { useCustomerVisitsList } from './service';
import moment from 'moment';
import 'moment/locale/tr';
import VisitCreate from './modals/visit-create';
import { KTSVG } from 'src/_metronic/helpers';
import Restricted from 'src/app/permissions/restricted';
import usePermission from 'src/app/permissions/use-permission';

/**
 * Müşteri Ziyaretleri Dashboard Widget
 * Farklı zaman dilimlerinde ziyaretleri listeler
 */

type TabType = 'day' | 'week' | 'lastWeek' | 'month' | 'quarter' | 'year';

interface Visit {
  id: string | number;
  customer_name?: string;
  sales_staff_name?: string;
  sales_staff_surname?: string;
  meeting_date: string;
  meeting_location?: string;
}

const locationColors: Record<string, string> = {
  Online: 'badge-light-info',
  'Müşteri Yerine': 'badge-light-primary',
  'Aynet Ofisi': 'badge-light-success',
};

// Tab seçenekleri
const tabOptions: { value: TabType; label: string; shortLabel: string }[] = [
  { value: 'day', label: 'Bugün', shortLabel: 'Bugün' },
  { value: 'week', label: 'Bu Hafta', shortLabel: 'Bu Hafta' },
  { value: 'lastWeek', label: 'Geçen Hafta', shortLabel: 'Geçen Hafta' },
  { value: 'month', label: 'Bu Ay', shortLabel: 'Bu Ay' },
  { value: 'quarter', label: 'Bu Çeyrek', shortLabel: 'Çeyrek' },
  { value: 'year', label: 'Bu Yıl', shortLabel: 'Yıl' },
];

// Tarih formatı için helper fonksiyon
const formatRelativeDate = (date: string) => {
  const now = moment().startOf('day');
  const target = moment(date).startOf('day');
  const diffDays = target.diff(now, 'days');

  if (diffDays === 0) return 'Bugün';
  if (diffDays === -1) return 'Dün';
  if (diffDays === 1) return 'Yarın';
  if (diffDays < 0) return `${Math.abs(diffDays)} gün önce`;
  return `${diffDays} gün sonra`;
};

export const CustomerVisitsWidget: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('week');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // İzin kontrolü
  const [permLoading, hasViewPermission] = usePermission('ROLE_CRM_CUSTOMER_VISITS');

  // Tarih aralıklarını hesapla
  const dateFilters = useMemo(() => {
    const today = moment();

    return {
      day: {
        start_date: today.format('YYYY-MM-DD'),
        end_date: today.format('YYYY-MM-DD'),
      },
      week: {
        start_date: today.clone().startOf('isoWeek').format('YYYY-MM-DD'),
        end_date: today.clone().endOf('isoWeek').format('YYYY-MM-DD'),
      },
      lastWeek: {
        start_date: today.clone().subtract(1, 'week').startOf('isoWeek').format('YYYY-MM-DD'),
        end_date: today.clone().subtract(1, 'week').endOf('isoWeek').format('YYYY-MM-DD'),
      },
      month: {
        start_date: today.clone().startOf('month').format('YYYY-MM-DD'),
        end_date: today.clone().endOf('month').format('YYYY-MM-DD'),
      },
      quarter: {
        start_date: today.clone().startOf('quarter').format('YYYY-MM-DD'),
        end_date: today.clone().endOf('quarter').format('YYYY-MM-DD'),
      },
      year: {
        start_date: today.clone().startOf('year').format('YYYY-MM-DD'),
        end_date: today.clone().endOf('year').format('YYYY-MM-DD'),
      },
    };
  }, []);

  // Aktif filtreye göre veriyi çek
  const {
    data: currentData,
    isLoading,
    refetch,
  } = useCustomerVisitsList({
    limit: 5,
    sortBy: 'desc',
    ...dateFilters[activeTab],
  });

  // İzin yoksa widget'i gösterme
  if (!permLoading && !hasViewPermission) {
    return null;
  }

  // Tüm listeleri yenile
  const refetchAll = () => {
    refetch();
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="col-xl-6">
        <div className="card card-xl-stretch mb-xl-8">
          <div className="card-header border-0 pt-5 bg-primary bg-opacity-25 pb-2">
            <h3 className="card-title align-items-start flex-column">
              <span className="card-label fw-bolder fs-3 mb-1">Müşteri Ziyaretleri</span>
              <span className="text-muted mt-1 fw-bold fs-7">Yükleniyor...</span>
            </h3>
          </div>
          <div className="card-body py-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="d-flex align-items-center mb-7">
                <div className="placeholder-glow me-4">
                  <div className="placeholder bg-secondary rounded" style={{ width: '45px', height: '45px' }}></div>
                </div>
                <div className="d-flex flex-column flex-grow-1">
                  <div className="placeholder-glow">
                    <span className="placeholder col-7 bg-secondary mb-2"></span>
                    <span className="placeholder col-5 bg-secondary"></span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const visits = currentData?.results || [];
  const totalResults = currentData?.totalResults || 0;

  const getTabLabel = () => {
    const option = tabOptions.find((o) => o.value === activeTab);
    return `${option?.label || ''} ${totalResults} ziyaret`;
  };

  const activeOption = tabOptions.find((o) => o.value === activeTab);

  const renderVisitRow = (visit: any) => (
    <tr key={visit.id}>
      <td>
        <span className="text-dark fw-bolder text-hover-primary d-block mb-1 fs-6">{visit.customer_name || 'İsimsiz Müşteri'}</span>
        <span className="text-muted fw-bold d-block fs-7">
          {visit.sales_staff_name} {visit.sales_staff_surname}
        </span>
      </td>
      <td className="text-end">
        <span className="fw-bolder text-dark d-block fs-7">{moment(visit.meeting_date).format('DD MMM YYYY')}</span>
        <span className="text-muted fw-bold d-block fs-8">{formatRelativeDate(visit.meeting_date)}</span>
      </td>
      <td className="text-end">
        <span className={`badge ${locationColors[visit.meeting_location] || 'badge-light-secondary'}`}>{visit.meeting_location || 'Belirtilmemiş'}</span>
      </td>
    </tr>
  );

  const renderEmptyState = () => (
    <tr>
      <td colSpan={5} className="text-center py-10">
        <div className="d-flex flex-column align-items-center">
          <KTSVG path="/media/icons/duotone/Interface/Calendar.svg" className="svg-icon-2x svg-icon-muted mb-3" />
          <span className="text-muted fs-6">{activeOption?.label} için ziyaret kaydı bulunamadı</span>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="col-xl-6">
      {/*begin::Tables Widget 5*/}
      <div className="card card-xl-stretch mb-xl-8">
        {/*begin::Header*/}
        <div className="card-header border-0 pt-5 bg-primary bg-opacity-25 pb-2">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bolder fs-3 mb-1">Müşteri Ziyaretleri</span>
            <span className="text-muted mt-1 fw-bold fs-7">{getTabLabel()}</span>
          </h3>
          <div className="card-toolbar d-flex align-items-center gap-2">
            {/* Dropdown Filtre */}
            <div className="position-relative">
              <button type="button" className="btn btn-sm btn-light-primary d-flex align-items-center gap-2 fw-bold" onClick={() => setShowDropdown(!showDropdown)}>
                <KTSVG path="/media/icons/duotone/Interface/Calendar.svg" className="svg-icon-6" />
                <span>{activeOption?.shortLabel}</span>
                <KTSVG path={`/media/icons/duotone/Navigation/Angle-${showDropdown ? 'up' : 'down'}.svg`} className="svg-icon-8" />
              </button>
              {showDropdown && (
                <>
                  <div className="position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 99 }} onClick={() => setShowDropdown(false)}></div>
                  <div className="position-absolute end-0 mt-1 py-2 bg-white rounded shadow-sm border" style={{ zIndex: 100, minWidth: '140px' }}>
                    {tabOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`dropdown-item px-4 py-2 d-flex align-items-center justify-content-between ${
                          activeTab === option.value ? 'active bg-light-primary text-primary' : ''
                        }`}
                        onClick={() => {
                          setActiveTab(option.value);
                          setShowDropdown(false);
                        }}
                      >
                        <span>{option.label}</span>
                        {activeTab === option.value && <KTSVG path="/media/icons/duotone/Navigation/Check.svg" className="svg-icon-6 svg-icon-primary" />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <Restricted to="ROLE_CRM_CUSTOMER_VISITS_CREATE">
              <button
                type="button"
                className="btn btn-sm btn-primary d-flex align-items-center justify-content-center"
                style={{ width: '32px', height: '32px', padding: 0 }}
                onClick={() => setShowCreateModal(true)}
              >
                <KTSVG path="/media/icons/duotone/Interface/Plus-Square.svg" className="svg-icon svg-icon-2 m-0" />
              </button>
            </Restricted>
          </div>
        </div>
        {/*end::Header*/}
        {/*begin::Body*/}
        <div className="card-body py-3">
          {/*begin::Table container*/}
          <div className="table-responsive">
            {/*begin::Table*/}
            <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4">
              {/*begin::Table head*/}
              <thead>
                <tr className="border-0">
                  <th className="p-0 min-w-150px" />
                  <th className="p-0 min-w-100px" />
                  <th className="p-0 min-w-100px" />
                </tr>
              </thead>
              {/*end::Table head*/}
              {/*begin::Table body*/}
              <tbody>{visits.length > 0 ? visits.map(renderVisitRow) : renderEmptyState()}</tbody>
              {/*end::Table body*/}
            </table>
          </div>
          {/*end::Table*/}
          {totalResults > 5 && (
            <div className="d-flex justify-content-center mt-4">
              <a href="/customer-visits" className="btn btn-sm btn-light-primary">
                Tümünü Görüntüle ({totalResults})
              </a>
            </div>
          )}
        </div>
        {/*end::Body*/}
      </div>
      {/*end::Tables Widget 5*/}

      {/* Ziyaret Ekleme Modal */}
      <VisitCreate show={showCreateModal} handleClose={() => setShowCreateModal(false)} refetchList={refetchAll} />
    </div>
  );
};

export default CustomerVisitsWidget;
